import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { findSourceSites, getSourceLibraries } from "@/api/filth-source-control-api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { sourceControlKey as key, sourceControlError } from "./filth-source-control-utils"

export function FilthSharePointSitePicker({
  backendId,
  app,
  site,
  drive,
  onSite,
  onDrive,
}: {
  backendId: string
  app: string
  site: string
  drive: string
  onSite: (id: string) => void
  onDrive: (id: string) => void
}) {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")
  const [search, setSearch] = useState<string | null>(null)
  const sites = useQuery({
    queryKey: ["filth-sites", backendId, app, search],
    queryFn: () => findSourceSites(backendId, app, search ?? ""),
    enabled: search !== null && !!app,
    retry: false,
  })
  const libraries = useQuery({
    queryKey: ["filth-libraries", backendId, app, site],
    queryFn: () => getSourceLibraries(backendId, app, site),
    enabled: !!app && !!site,
    retry: false,
  })
  const error = sites.error || libraries.error
  return (
    <div className="space-y-4">
      <Label className="grid gap-2">
        {t(`${key}.siteQuery`)}
        <Input value={query} onChange={(e) => setQuery(e.target.value)} />
      </Label>
      <p className="text-muted-foreground text-sm">{t(`${key}.siteHint`)}</p>
      <Button
        disabled={!app || sites.isFetching}
        type="button"
        variant="outline"
        onClick={() => {
          onSite("")
          onDrive("")
          setSearch(query)
          if (search === query) void sites.refetch()
        }}
      >
        {t(`${key}.findSites`)}
      </Button>
      {(sites.data?.truncated || libraries.data?.truncated) && (
        <p className="text-muted-foreground text-sm" role="status">
          {t(`${key}.pickerTruncated`)}
        </p>
      )}
      {error && <p role="alert">{t(`${key}.errors.${sourceControlError(error)}`)}</p>}
      {sites.data && (
        <Label className="grid gap-2">
          {t(`${key}.site`)}
          <select
            aria-label={t(`${key}.site`)}
            className="bg-background rounded-md border p-2 text-sm"
            value={site}
            onChange={(e) => {
              onSite(e.target.value)
              onDrive("")
            }}
          >
            <option value="">{t(`${key}.choose`)}</option>
            {sites.data.sites.map((v) => (
              <option key={v.site_id} value={v.site_id}>
                {v.name}
              </option>
            ))}
          </select>
        </Label>
      )}
      {site && (
        <Label className="grid gap-2">
          {t(`${key}.library`)}
          <select
            aria-label={t(`${key}.library`)}
            className="bg-background rounded-md border p-2 text-sm"
            disabled={libraries.isFetching}
            value={drive}
            onChange={(e) => onDrive(e.target.value)}
          >
            <option value="">{t(`${key}.choose`)}</option>
            {libraries.data?.libraries.map((v) => (
              <option key={v.drive_id} value={v.drive_id}>
                {v.name}
              </option>
            ))}
          </select>
        </Label>
      )}
    </div>
  )
}
